import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '../StyledTypography';
import IDSSubformRenderer from '../FormCrafterFormRenderer/renderers/IDSSubformRenderer';
import CircularProgress from 'apollo-react/components/CircularProgress';
import { validateField } from '../../../utils/formCrafter/formatValidationError';
import { FIELD_TYPES } from '../../../constants/formCrafter.constant';
import styles from './SubformPreview.module.css';

const noop = () => {};

/**
 * Generates initial values so repeater fields start
 * with one sample item (child fields visible).
 */
const buildInitialValues = (fields) => {
  if (!Array.isArray(fields)) return {};
  const values = {};
  fields.forEach((field) => {
    const isRepeater =
      field.type === FIELD_TYPES.REPEATER ||
      field.type?.toLowerCase() === 'repeater' ||
      field.type?.toLowerCase() === 'repeatable';
    if (isRepeater && Array.isArray(field.fields)) {
      values[field.id] = [{ id: 'preview_item' }];
    }
  });
  return values;
};

/**
 * SubformPreview - Reusable organism that renders a subform schema
 * using IDS components.
 *
 * When disabled (default), fields are read-only with static values.
 * When enabled, fields are fully interactive with state management,
 * supporting repeater add/remove, conditional visibility, and validation
 * using the same validateField utility as the request submission flow.
 *
 * @param {Object} schema - Parsed FormCrafter schema ({ title, description, fields })
 * @param {boolean} loading - Whether data is still loading
 * @param {boolean} disabled - Whether fields are disabled (default: true for read-only)
 */
const SubformPreview = ({ schema, loading = false, disabled = true }) => {
  const { t } = useTranslation();

  const initialValues = useMemo(
    () => (disabled ? buildInitialValues(schema?.fields) : {}),
    [schema, disabled]
  );

  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const valuesRef = useRef(values);
  valuesRef.current = values;
  const touchedRef = useRef(touched);
  touchedRef.current = touched;

  const validateAndSetError = useCallback(
    (fieldId, value) => {
      const field = schema?.fields?.find((f) => f.id === fieldId);
      if (!field) return;
      const error = validateField(value, field, t);
      setErrors((prev) => {
        if (error) return { ...prev, [fieldId]: error };
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    },
    [schema, t]
  );

  const handleFieldChange = useCallback(
    (fieldId, newValue) => {
      setValues((prev) => ({ ...prev, [fieldId]: newValue }));
      if (touchedRef.current[fieldId]) {
        validateAndSetError(fieldId, newValue);
      }
    },
    [validateAndSetError]
  );

  const handleFieldBlur = useCallback(
    (fieldId) => {
      setTouched((prev) => ({ ...prev, [fieldId]: true }));
      validateAndSetError(fieldId, valuesRef.current[fieldId]);
    },
    [validateAndSetError]
  );

  const noopFieldChange = useCallback(() => noop, []);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <CircularProgress />
      </div>
    );
  }

  if (!schema || !schema.fields || schema.fields.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="body2" className={styles.emptyText}>
          {t('no_form_fields')}
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <IDSSubformRenderer
        schema={schema}
        values={disabled ? initialValues : values}
        onChange={disabled ? noopFieldChange : handleFieldChange}
        onBlur={disabled ? noop : handleFieldBlur}
        errors={disabled ? {} : errors}
        touched={disabled ? {} : touched}
        disabled={disabled}
        showTitle={true}
      />
    </div>
  );
};

export default SubformPreview;
